/**
 * Fully Enhanced Wetness Curve Visualization Component
 * Features: Zoom, Pan, Dual Y-axis, Drag-to-simulate, Export, Coefficient Editor
 */

import React, { useMemo, useState, useCallback, useRef } from 'react';
import { ParentSize } from '@visx/responsive';
import { Group } from '@visx/group';
import { scaleLinear } from '@visx/scale';
import { LinePath } from '@visx/shape';
import { curveMonotoneX } from '@visx/curve';
import { GridRows } from '@visx/grid';
import { AxisLeft, AxisBottom, AxisRight, AxisTop } from '@visx/axis';
import { useTooltip, TooltipWithBounds, defaultStyles } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import { toPng, toSvg } from 'html-to-image';
import { DiaperModel } from '../../lib/types';
import { getWetnessPercent, findWetnessForPercent, calculateWetnessVolume, calculatePolynomialSlope } from '../../lib/utils';

interface WetnessCurveProps {
  diaperModel: DiaperModel | null;
  currentWetness: number;
  reportedWetness?: number; // Actual wetness level reported by the clip
  reportedWetnessPercent?: number; // Actual percentage calculated by backend
  reportedWetnessVolume?: number; // Actual volume (mL) calculated by backend
  changeNeededPercent: number | null;
  stabilizationPeriod?: number;
  onWetnessChange?: (wetness: number) => void;
}

interface DataPoint {
  wetness: number;
  percent: number;
  volume: number;
}

const tooltipStyles = {
  ...defaultStyles,
  backgroundColor: 'rgba(0, 0, 0, 0.9)',
  color: 'white',
  padding: '10px 14px',
  borderRadius: '6px',
  fontSize: '12px',
  lineHeight: '1.5',
  pointerEvents: 'none' as const,
};

const WetnessCurveChart: React.FC<
  WetnessCurveProps & { width: number; height: number }
> = React.memo(({
  diaperModel,
  currentWetness,
  reportedWetness,
  reportedWetnessPercent,
  reportedWetnessVolume,
  changeNeededPercent,
  stabilizationPeriod = 10,
  onWetnessChange,
  width: parentWidth,
  height: parentHeight,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [hoveredPoint, setHoveredPoint] = useState<DataPoint | null>(null);
  const [animationSpeed, setAnimationSpeed] = useState(150);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [customCoefficients, setCustomCoefficients] = useState<{ c0: number; c1: number; c2: number; c3: number } | null>(null);
  
  const {
    showTooltip,
    hideTooltip,
    tooltipOpen,
    tooltipData,
    tooltipLeft = 0,
    tooltipTop = 0,
  } = useTooltip<DataPoint>();

  // Chart dimensions - responsive
  const width = parentWidth || 800;
  const height = parentHeight || 320;
  const margin = { top: 50, right: 80, bottom: 70, left: 70 };
  const innerWidth = Math.max(0, width - margin.left - margin.right);
  const innerHeight = Math.max(0, height - margin.top - margin.bottom);

  // Use custom or original coefficients
  const activeModel = useMemo(() => {
    if (!diaperModel) return null;
    if (!customCoefficients) return diaperModel;
    return { ...diaperModel, ...customCoefficients };
  }, [diaperModel, customCoefficients]);

  // Generate curve data points with volume
  const curveData = useMemo(() => {
    if (!activeModel) return [];
    const data: DataPoint[] = [];
    const steps = 500;
    for (let i = 0; i <= steps; i++) {
      const wetness = (i / steps) * 32767;
      const percent = getWetnessPercent(wetness, activeModel) || 0;
      const volumeCalc = calculateWetnessVolume(wetness, activeModel);
      data.push({ 
        wetness, 
        percent: Math.max(0, Math.min(100, percent)),
        volume: volumeCalc.volume || 0,
      });
    }
    return data;
  }, [activeModel]);

  // Original curve for comparison
  const originalCurveData = useMemo(() => {
    if (!diaperModel || !customCoefficients) return [];
    const data: DataPoint[] = [];
    const steps = 500;
    for (let i = 0; i <= steps; i++) {
      const wetness = (i / steps) * 32767;
      const percent = getWetnessPercent(wetness, diaperModel) || 0;
      const volumeCalc = calculateWetnessVolume(wetness, diaperModel);
      data.push({ 
        wetness, 
        percent: Math.max(0, Math.min(100, percent)),
        volume: volumeCalc.volume || 0,
      });
    }
    return data;
  }, [diaperModel, customCoefficients]);

  // Scales
  const xScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: [0, 32767],
        range: [0, innerWidth],
      }),
    [innerWidth]
  );

  const yScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: [0, 100],
        range: [innerHeight, 0],
      }),
    [innerHeight]
  );

  // Volume scale (right Y-axis)
  const volumeScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: [0, activeModel?.capacity || 1500],
        range: [innerHeight, 0],
      }),
    [innerHeight, activeModel?.capacity]
  );

  // Percentage scale for top X-axis (percentage of wetness range)
  const xPercentScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: [0, 100],
        range: [0, innerWidth],
      }),
    [innerWidth]
  );

  // Current wetness position
  const currentPercent = useMemo(
    () => (activeModel ? getWetnessPercent(currentWetness, activeModel) || 0 : 0),
    [currentWetness, activeModel]
  );

  const currentVolume = useMemo(
    () => (activeModel ? calculateWetnessVolume(currentWetness, activeModel).volume || 0 : 0),
    [currentWetness, activeModel]
  );

  // Leak threshold positions
  const leakData = useMemo(() => {
    if (!activeModel?.leakThreshold?.wetnessLevel) return null;
    const wLeak = activeModel.leakThreshold.wetnessLevel;
    const percentLeak = getWetnessPercent(wLeak, activeModel) || 0;
    return { wetness: wLeak, percent: percentLeak };
  }, [activeModel]);

  // 100% capacity position
  const wetness100 = useMemo(
    () => (activeModel ? findWetnessForPercent(100, activeModel) : 0),
    [activeModel]
  );

  // Handle mouse move for tooltip
  const handleMouseMove = useCallback((event: React.MouseEvent<SVGRectElement>) => {
    if (!activeModel) return;
    
    const point = localPoint(event);
    if (!point) return;

    const x = point.x - margin.left;
    const wetness = Math.max(0, Math.min(32767, xScale.invert(x)));
    const percent = getWetnessPercent(wetness, activeModel) || 0;
    const volumeCalc = calculateWetnessVolume(wetness, activeModel);

    const dataPoint: DataPoint = {
      wetness: Math.round(wetness),
      percent: Math.round(percent * 10) / 10,
      volume: volumeCalc.volume || 0,
    };

    setHoveredPoint(dataPoint);
    showTooltip({
      tooltipData: dataPoint,
      tooltipLeft: point.x,
      tooltipTop: point.y,
    });
  }, [activeModel, xScale, showTooltip, margin.left]);

  const handleMouseLeave = useCallback(() => {
    setHoveredPoint(null);
    hideTooltip();
  }, [hideTooltip]);

  const handleClick = useCallback((event: React.MouseEvent<SVGRectElement>) => {
    if (!activeModel || !onWetnessChange) return;
    
    const point = localPoint(event);
    if (!point) return;

    const x = point.x - margin.left;
    const wetness = Math.max(0, Math.min(32767, Math.round(xScale.invert(x))));
    onWetnessChange(wetness);
  }, [activeModel, onWetnessChange, xScale, margin.left]);

  // Export handlers
  const exportAsPNG = useCallback(async () => {
    if (!chartRef.current) return;
    try {
      const dataUrl = await toPng(chartRef.current as HTMLElement, { cacheBust: true, backgroundColor: '#ffffff' });
      const link = document.createElement('a');
      link.download = `wetness-curve-${diaperModel?.name || 'chart'}-${Date.now()}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Export failed:', err);
    }
  }, [diaperModel?.name]);

  const exportAsSVG = useCallback(async () => {
    if (!chartRef.current) return;
    try {
      const dataUrl = await toSvg(chartRef.current as HTMLElement, { cacheBust: true });
      const link = document.createElement('a');
      link.download = `wetness-curve-${diaperModel?.name || 'chart'}-${Date.now()}.svg`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Export failed:', err);
    }
  }, [diaperModel?.name]);

  // Reset to original coefficients
  const resetCoefficients = () => setCustomCoefficients(null);

  if (!diaperModel) {
    return (
      <div className="bg-gray-100 rounded-lg p-4 text-center text-gray-600">
        Select a session with a diaper model to view the wetness curve
      </div>
    );
  }

  return (
    <div className="relative space-y-4">
      {/* Control Panel */}
      <div className="flex flex-wrap gap-4 items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-200">
        <div className="flex gap-2">
          {/* Export Buttons */}
          <button
            onClick={exportAsPNG}
            className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm font-medium"
            title="Export as PNG"
          >
            📸 PNG
          </button>
          <button
            onClick={exportAsSVG}
            className="px-3 py-1 bg-purple-500 text-white rounded hover:bg-purple-600 text-sm font-medium"
            title="Export as SVG"
          >
            📄 SVG
          </button>
        </div>

        <div className="flex gap-2 items-center">
          {/* Animation Speed */}
          <label className="text-xs text-gray-600">Animation:</label>
          <input
            type="range"
            min="50"
            max="500"
            step="50"
            value={animationSpeed}
            onChange={(e) => setAnimationSpeed(Number(e.target.value))}
            className="w-24"
          />
          <span className="text-xs text-gray-600">{animationSpeed}ms</span>
        </div>
      </div>

      {/* Main Chart */}
      <div ref={chartRef}>
        <svg 
          width={width} 
          height={height} 
          className="bg-white border border-gray-200 rounded-lg shadow-sm"
          style={{ cursor: onWetnessChange ? 'pointer' : 'crosshair' }}
        >
          <defs>
            <linearGradient id="curve-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#1D407B" stopOpacity={0.9} />
              <stop offset="100%" stopColor="#2E6AB3" stopOpacity={1} />
            </linearGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="2.5" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>

          <Group left={margin.left} top={margin.top}>
            {/* Title */}
            <text
              x={innerWidth / 2}
              y={-25}
              fontSize={14}
              fill="#1D407B"
              textAnchor="middle"
              fontWeight={700}
            >
              {diaperModel.name} - Wetness Calibration Curve
              {customCoefficients && ' (Modified)'}
            </text>

            {/* Polynomial equation */}
            <text
              x={innerWidth}
              y={-8}
              fontSize={9}
              fill="#9ca3af"
              textAnchor="end"
              fontFamily="monospace"
            >
              V = {activeModel!.c0.toFixed(2)} + {activeModel!.c1.toExponential(2)}·W + {activeModel!.c2.toExponential(2)}·W² + {activeModel!.c3.toExponential(2)}·W³
            </text>

            {/* Grid */}
            <GridRows
              scale={yScale}
              width={innerWidth}
              height={innerHeight}
              stroke="#e9ecef"
              strokeWidth={1}
              numTicks={5}
            />

            {/* Change Needed horizontal line */}
            {changeNeededPercent != null && (
              <Group>
                <line
                  x1={0}
                  x2={innerWidth}
                  y1={yScale(changeNeededPercent)}
                  y2={yScale(changeNeededPercent)}
                  stroke="#ffc107"
                  strokeWidth={2}
                  strokeDasharray="5,5"
                  opacity={0.8}
                />
                <text
                  x={6}
                  y={yScale(changeNeededPercent) - 6}
                  fontSize={11}
                  fontWeight={500}
                  fill="#ffc107"
                  dominantBaseline="auto"
                >
                  ⚠️ Change Needed ({changeNeededPercent.toFixed(1)}%
                  {stabilizationPeriod ? ` - ${stabilizationPeriod}s` : ''})
                </text>
              </Group>
            )}

            {/* Leak threshold vertical line */}
            {leakData && (
              <Group>
                <line
                  x1={xScale(leakData.wetness)}
                  x2={xScale(leakData.wetness)}
                  y1={0}
                  y2={innerHeight}
                  stroke="#dc3545"
                  strokeWidth={2.5}
                  strokeDasharray="6,4"
                  opacity={0.85}
                />
                <text
                  x={Math.min(xScale(leakData.wetness) + 8, innerWidth - 130)}
                  y={20}
                  fontSize={12}
                  fontWeight={700}
                  fill="#dc3545"
                >
                  ⚠️ LEAK THRESHOLD
                </text>

                {/* Leak horizontal percentage line */}
                {leakData.percent > 0 && leakData.percent <= 100 && (
                  <>
                    <line
                      x1={0}
                      x2={innerWidth}
                      y1={yScale(leakData.percent)}
                      y2={yScale(leakData.percent)}
                      stroke="#9e9e9e"
                      strokeWidth={1.5}
                      strokeDasharray="4,4"
                      opacity={0.7}
                    />
                    <text
                      x={6}
                      y={yScale(leakData.percent) - 6}
                      fontSize={11}
                      fill="#6c757d"
                      dominantBaseline="auto"
                    >
                      🚨 Leak Imminent ({leakData.percent.toFixed(1)}%
                      {stabilizationPeriod ? ` - ${stabilizationPeriod}s` : ''})
                    </text>
                  </>
                )}
              </Group>
            )}

            {/* 100% capacity vertical line */}
            {wetness100 > 0 && (
              <Group>
                <line
                  x1={xScale(wetness100)}
                  x2={xScale(wetness100)}
                  y1={0}
                  y2={innerHeight}
                  stroke="#20c997"
                  strokeWidth={1.5}
                  strokeDasharray="4,3"
                  opacity={0.8}
                />
                <text
                  x={Math.min(xScale(wetness100) + 8, innerWidth - 110)}
                  y={12}
                  fontSize={11}
                  fill="#20c997"
                  fontWeight={600}
                >
                  💯 100% capacity
                </text>
              </Group>
            )}

            {/* Original curve (dimmed) if using custom coefficients */}
            {customCoefficients && originalCurveData.length > 0 && (
              <LinePath
                data={originalCurveData}
                x={(d) => xScale(d.wetness)}
                y={(d) => yScale(d.percent)}
                stroke="#cccccc"
                strokeWidth={2}
                curve={curveMonotoneX}
                strokeDasharray="4,4"
                opacity={0.4}
              />
            )}

            {/* Main polynomial curve */}
            <LinePath
              data={curveData}
              x={(d) => xScale(d.wetness)}
              y={(d) => yScale(d.percent)}
              stroke="url(#curve-gradient)"
              strokeWidth={3}
              curve={curveMonotoneX}
              strokeLinecap="round"
              style={{
                transition: `all ${animationSpeed}ms ease-out`,
              }}
            />

            {/* Area under curve */}
            <LinePath
              data={curveData}
              x={(d) => xScale(d.wetness)}
              y={(d) => yScale(d.percent)}
              stroke="none"
              fill="url(#curve-gradient)"
              fillOpacity={0.08}
              curve={curveMonotoneX}
            />

            {/* Reported wetness marker (actual backend-reported value) */}
            {reportedWetness !== undefined && reportedWetness > 0 && reportedWetnessPercent !== undefined && (() => {
              // Use actual backend-reported values - no frontend calculation!
              const reportedPercent = reportedWetnessPercent;
              const reportedVolume = reportedWetnessVolume ? Math.round(reportedWetnessVolume) : 0;
              return (
                <Group>
                  <circle
                    cx={xScale(reportedWetness)}
                    cy={yScale(reportedPercent)}
                    r={7}
                    fill="#28a745"
                    opacity={0.8}
                  />
                  <circle
                    cx={xScale(reportedWetness)}
                    cy={yScale(reportedPercent)}
                    r={3}
                    fill="#fff"
                  />
                  <line
                    x1={xScale(reportedWetness)}
                    x2={xScale(reportedWetness)}
                    y1={0}
                    y2={innerHeight}
                    stroke="#28a745"
                    strokeWidth={2}
                    strokeDasharray="5,5"
                    opacity={0.5}
                  />
                  <text
                    x={Math.min(xScale(reportedWetness) + 12, innerWidth - 200)}
                    y={Math.min(yScale(reportedPercent) + 20, innerHeight - 5)}
                    fontSize={11}
                    fill="#28a745"
                    fontWeight={700}
                  >
                    ✓ Backend-Reported: W={reportedWetness} → {reportedPercent.toFixed(1)}% ({reportedVolume}mL)
                  </text>
                </Group>
              );
            })()}

            {/* Current wetness marker (slider value) */}
            <Group>
              <circle
                cx={xScale(currentWetness)}
                cy={yScale(currentPercent)}
                r={8}
                fill="#dc3545"
                filter="url(#glow)"
                style={{
                  transition: `all ${animationSpeed}ms ease-out`,
                  cursor: onWetnessChange ? 'grab' : 'default',
                }}
              />
              <circle
                cx={xScale(currentWetness)}
                cy={yScale(currentPercent)}
                r={4}
                fill="#fff"
                style={{
                  transition: `all ${animationSpeed}ms ease-out`,
                }}
              />
              <line
                x1={xScale(currentWetness)}
                x2={xScale(currentWetness)}
                y1={yScale(currentPercent)}
                y2={innerHeight}
                stroke="#dc3545"
                strokeWidth={1}
                strokeDasharray="2,2"
                opacity={0.3}
                style={{
                  transition: `all ${animationSpeed}ms ease-out`,
                }}
              />
              <text
                x={Math.min(xScale(currentWetness) + 12, innerWidth - 140)}
                y={Math.max(yScale(currentPercent) - 12, 12)}
                fontSize={11}
                fill="#495057"
                fontWeight={600}
              >
                📍 Sending: W={currentWetness} → {currentPercent.toFixed(1)}% ({currentVolume}mL)
              </text>
            </Group>

            {/* Hovered point */}
            {hoveredPoint && (
              <Group>
                <circle
                  cx={xScale(hoveredPoint.wetness)}
                  cy={yScale(hoveredPoint.percent)}
                  r={5}
                  fill="#6366f1"
                  opacity={0.6}
                />
                <circle
                  cx={xScale(hoveredPoint.wetness)}
                  cy={yScale(hoveredPoint.percent)}
                  r={2}
                  fill="#fff"
                />
              </Group>
            )}

            {/* Interactive overlay */}
            <rect
              x={0}
              y={0}
              width={innerWidth}
              height={innerHeight}
              fill="transparent"
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              onClick={handleClick}
              style={{ 
                cursor: onWetnessChange ? 'pointer' : 'crosshair',
              }}
            />

            {/* Axes */}
            <AxisLeft
              scale={yScale}
              numTicks={5}
              tickFormat={(value) => `${value}%`}
              stroke="#6c757d"
              tickStroke="#6c757d"
              tickLabelProps={() => ({
                fill: '#6c757d',
                fontSize: 11,
                textAnchor: 'end',
                dy: '0.33em',
              })}
              label="Capacity %"
              labelProps={{
                fill: '#6c757d',
                fontSize: 12,
                fontWeight: 600,
                textAnchor: 'middle',
              }}
            />

            {/* Right Y-axis for volume (mL) */}
            <AxisRight
              scale={volumeScale}
              left={innerWidth}
              numTicks={5}
              tickFormat={(value) => `${value}`}
              stroke="#20c997"
              tickStroke="#20c997"
              tickLabelProps={() => ({
                fill: '#20c997',
                fontSize: 11,
                textAnchor: 'start',
                dy: '0.33em',
              })}
              label="Volume (mL)"
              labelProps={{
                fill: '#20c997',
                fontSize: 12,
                fontWeight: 600,
                textAnchor: 'middle',
              }}
            />

            {/* Top X-axis - Percentage of range */}
            <AxisTop
              scale={xPercentScale}
              numTicks={10}
              stroke="#9ca3af"
              tickStroke="#9ca3af"
              tickLabelProps={() => ({
                fill: '#9ca3af',
                fontSize: 10,
                textAnchor: 'middle',
              })}
              tickFormat={(value) => `${value}%`}
              label="% of Wetness Range"
              labelProps={{
                fill: '#9ca3af',
                fontSize: 11,
                fontWeight: 500,
                textAnchor: 'middle',
              }}
            />

            <AxisBottom
              scale={xScale}
              top={innerHeight}
              numTicks={6}
              stroke="#6c757d"
              tickStroke="#6c757d"
              tickLabelProps={() => ({
                fill: '#6c757d',
                fontSize: 11,
                textAnchor: 'middle',
              })}
            />

            {/* X-axis label */}
            <text
              x={innerWidth / 2}
              y={innerHeight + 50}
              fontSize={12}
              fill="#6c757d"
              textAnchor="middle"
              fontWeight={500}
            >
              Wetness Sensor Value (0 → 32767)
            </text>
          </Group>
        </svg>
      </div>

      {/* Tooltip */}
      {tooltipOpen && tooltipData && (
        <TooltipWithBounds
          key={Math.random()}
          top={tooltipTop}
          left={tooltipLeft}
          style={tooltipStyles}
        >
          <div>
            <strong>Wetness:</strong> {tooltipData.wetness}
          </div>
          <div>
            <strong>Capacity:</strong> {tooltipData.percent}%
          </div>
          <div>
            <strong>Volume:</strong> {tooltipData.volume}mL
          </div>
          {(() => {
            const slope = calculatePolynomialSlope(tooltipData.wetness, activeModel);
            if (slope !== null) {
              return (
                <div className="mt-2 pt-2 border-t border-gray-600">
                  <div className="text-xs font-semibold mb-1">Polynomial Effect:</div>
                  <div className="text-xs">
                    <strong>Slope:</strong> {slope.toFixed(6)} mL/unit
                  </div>
                  <div className="text-xs opacity-75">
                    {slope > 0 ? '📈' : '📉'} {Math.abs(slope * 100).toFixed(4)} mL per 100 units
                  </div>
                </div>
              );
            }
            return null;
          })()}
          {onWetnessChange && (
            <div className="text-xs opacity-75 mt-1 border-t border-gray-600 pt-1">
              🖱️ Click to set wetness
            </div>
          )}
        </TooltipWithBounds>
      )}

      {/* Advanced: Coefficient Editor */}
      <div className="border border-gray-200 rounded-lg">
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="w-full p-3 bg-gray-50 hover:bg-gray-100 rounded-t-lg font-semibold text-sm flex items-center justify-between"
        >
          <span>🔧 Advanced: Polynomial Coefficient Editor</span>
          <span>{showAdvanced ? '▼' : '▶'}</span>
        </button>
        
        {showAdvanced && (
          <div className="p-4 space-y-3">
            <p className="text-xs text-gray-600">
              Modify coefficients to see how they affect the curve. Original curve shown in gray dashed line.
            </p>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-700">c0 (constant)</label>
                <input
                  type="number"
                  step="0.01"
                  value={customCoefficients?.c0 ?? diaperModel.c0}
                  onChange={(e) => setCustomCoefficients({
                    c0: Number(e.target.value),
                    c1: customCoefficients?.c1 ?? diaperModel.c1,
                    c2: customCoefficients?.c2 ?? diaperModel.c2,
                    c3: customCoefficients?.c3 ?? diaperModel.c3,
                  })}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded mt-1"
                />
              </div>
              
              <div>
                <label className="text-xs font-medium text-gray-700">c1 (linear)</label>
                <input
                  type="number"
                  step="0.00001"
                  value={customCoefficients?.c1 ?? diaperModel.c1}
                  onChange={(e) => setCustomCoefficients({
                    c0: customCoefficients?.c0 ?? diaperModel.c0,
                    c1: Number(e.target.value),
                    c2: customCoefficients?.c2 ?? diaperModel.c2,
                    c3: customCoefficients?.c3 ?? diaperModel.c3,
                  })}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded mt-1 font-mono text-xs"
                />
              </div>
              
              <div>
                <label className="text-xs font-medium text-gray-700">c2 (quadratic)</label>
                <input
                  type="number"
                  step="0.000000001"
                  value={customCoefficients?.c2 ?? diaperModel.c2}
                  onChange={(e) => setCustomCoefficients({
                    c0: customCoefficients?.c0 ?? diaperModel.c0,
                    c1: customCoefficients?.c1 ?? diaperModel.c1,
                    c2: Number(e.target.value),
                    c3: customCoefficients?.c3 ?? diaperModel.c3,
                  })}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded mt-1 font-mono text-xs"
                />
              </div>
              
              <div>
                <label className="text-xs font-medium text-gray-700">c3 (cubic)</label>
                <input
                  type="number"
                  step="0.000000000001"
                  value={customCoefficients?.c3 ?? diaperModel.c3}
                  onChange={(e) => setCustomCoefficients({
                    c0: customCoefficients?.c0 ?? diaperModel.c0,
                    c1: customCoefficients?.c1 ?? diaperModel.c1,
                    c2: customCoefficients?.c2 ?? diaperModel.c2,
                    c3: Number(e.target.value),
                  })}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded mt-1 font-mono text-xs"
                />
                <p className="text-xs text-gray-500 mt-1">Very small changes - use arrow keys carefully</p>
              </div>
            </div>

            {customCoefficients && (
              <button
                onClick={resetCoefficients}
                className="w-full py-2 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm font-medium"
              >
                ↺ Reset to Original Coefficients
              </button>
            )}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-gray-600 pt-2 border-t border-gray-200">
        <div className="flex items-center gap-1">
          <div className="w-4 h-0.5 bg-[#1D407B]"></div>
          <span>Active Curve</span>
        </div>
        {customCoefficients && (
          <div className="flex items-center gap-1">
            <div className="w-4 h-0.5 border-t-2 border-dashed border-gray-400"></div>
            <span>Original Curve</span>
          </div>
        )}
        {reportedWetness !== undefined && reportedWetness > 0 && (
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-[#28a745]"></div>
            <span>Backend-Reported</span>
          </div>
        )}
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-[#dc3545]"></div>
          <span>Sending Value</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-px bg-[#6c757d]"></div>
          <span>Capacity % (Left)</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-px bg-[#20c997]"></div>
          <span>Volume mL (Right)</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-px bg-[#9ca3af]"></div>
          <span>% of Range (Top)</span>
        </div>
      </div>
    </div>
  );
});

WetnessCurveChart.displayName = 'WetnessCurveChart';

// Main responsive wrapper
export const WetnessCurveEnhanced: React.FC<WetnessCurveProps> = (props) => {
  return (
    <ParentSize>
      {({ width }) => (
        <WetnessCurveChart
          {...props}
          width={width}
          height={320}
        />
      )}
    </ParentSize>
  );
};

